import type { Sensor } from "@station/domain/models/sensor.model";
import { t } from "i18next";

interface SensorLabelSource {
    readonly code?: string | null;
    readonly name: string;
}

interface IdentifiedSensorLabelSource extends SensorLabelSource {
    readonly id: number;
}

/**
 * Resolves the sensor's base display name: the translation of its code when
 * one exists, otherwise the backend-provided raw name. Never returns the raw
 * i18n key, even if the code has no translation for the active locale.
 */
export const getSensorLabel = (sensor: SensorLabelSource): string =>
    sensor.code
        ? t(`station:sensors.${sensor.code}`, { defaultValue: sensor.name })
        : sensor.name;

/**
 * Canonical series label used as the lookup key across the chart stack:
 * y-axis `variableIds`, `effectiveRanges`, etc.
 *
 * Producers (viewmodels) and consumers (chart resolvers) MUST agree on this
 * exact format, otherwise lookups fail silently. Keep this as the single
 * source of truth and route every label construction through it.
 *
 * Pass `label` to build the series key from a de-duplicated base label
 * (see getUniqueSensorLabels) instead of the plain translated one.
 *
 * Examples:
 *   - sensor.symbol = "°C"  → "Temperatura (°C)"
 *   - sensor.symbol = null  → "Temperatura"
 */
export const formatSeriesLabel = (
    sensor: Readonly<Sensor>,
    label: string = getSensorLabel(sensor),
): string => (sensor.symbol ? `${label} (${sensor.symbol})` : label);

/**
 * Translated labels are shared between variable groups (e.g. ICR_TOT01.VAL
 * and ICR_TOT02.VAL both resolve to the same text) but are used as object
 * keys for chart series, units and report columns, where duplicates would
 * silently merge. Returns a per-sensor-id label map where repeated labels
 * are disambiguated by appending the backend raw name, which is the unique
 * key that was used before translation.
 */
export const getUniqueSensorLabels = (
    sensors: readonly IdentifiedSensorLabelSource[],
): Map<number, string> => {
    const labelCount = new Map<string, number>();

    for (const sensor of sensors) {
        const label = getSensorLabel(sensor);
        labelCount.set(label, (labelCount.get(label) ?? 0) + 1);
    }

    return new Map(
        sensors.map((sensor) => {
            const label = getSensorLabel(sensor);

            return [
                sensor.id,
                (labelCount.get(label) ?? 0) > 1
                    ? `${label} (${sensor.name})`
                    : label,
            ];
        }),
    );
};
